import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Button, IconButton, TextField, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteThreatNotification, getThreatNotificationList, toggleThreatNotification } from '../../../../services/manage/threatnotification';
import { RootState } from '../../../../store';
import DataTable from '../../../DataDisplay/Table';
import ContentLayout from '../../../Layout/Content';
import { showDialog, showToast } from '../../../utils/notifications';
import { RecipientChips } from './ui';

const ManageThreat = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const isManager = authContext?.is_group_manager || authContext?.role === 'Owner' || authContext?.role === 'SubOwner';
    const region = useSelector((state: RootState) => state.mainReducer.region);
    const [threatNotificationList, setThreatNotificationList] = useState<any>([]);
    const [loading, setLoading] = useState(true);

    const handleToggleStatus = async (id: string, currentActive: boolean) => {
        const response = await toggleThreatNotification(region, authContext.token, id, !currentActive);
        if (response?.result?.code === '0000') {
            setThreatNotificationList((prevList: any) => prevList.map((item: any) => (item.id === id ? { ...item, status: !currentActive } : item)));
        }
    };

    useEffect(() => {
        if (!isManager) {
            showDialog({
                title: t('manage_threat_permission_dialog'),
                content: t('manage_threat_permission_dialog_description'),
                okText: t('ok'),
                onOk: () => {
                    navigate('/settings/notifications');
                },
                cancelButtonProps: {
                    hidden: true,
                },
            });
            return;
        }
        if (threatNotificationList.length > 0) return;
        const fetchThreatNotificationList = async () => {
            setLoading(true);
            const response = await getThreatNotificationList(region, authContext.token);
            if (response?.result?.code === '0000') {
                setThreatNotificationList(response.data.reverse());
            }
            setLoading(false);
        };
        fetchThreatNotificationList();
     
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteConfirm = (id: string) => {
        showDialog({
            title: t('delete_threat_confirm'),
            content: t('delete_threat_confirm_description'),
            okText: t('delete'),
            cancelText: t('cancel'),
            onOk: () => handleDelete(id),
        });
    };

    const handleDelete = async (id: string) => {
        const response = await deleteThreatNotification(region, authContext.token, id);
        if (response?.result?.code === '0000') {
            showToast('success', t('delete_threat_success'));
            setThreatNotificationList(threatNotificationList.filter((item: any) => item.id !== id));
        } else {
            showToast('error', t('delete_threat_failed'));
        }
    };

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('notification_name'), flex: 1 },
        { field: 'packageName', headerName: t('app_name'), flex: 1 },
        {
            field: 'rule',
            headerName: t('condition'),
            flex: 2,
            renderCell: (params) => (
                <Tooltip title={t(params.row.rule.code)}>
                    <span>{t(params.row.rule.code)}</span>
                </Tooltip>
            ),
        },
        { field: 'updatedAt', headerName: t('updated_on'), flex: 1 },
        {
            field: 'recipient',
            headerName: t('recipient'),
            flex: 2,
            renderCell: (params) => <RecipientChips emails={params.row.emails} slacks={params.row.slacks} groups={params.row.groups} />,
        },
        {
            field: 'action',
            headerName: t('action'),
            flex: 1,
            renderCell: (params) => (
                <div className="flex gap-2 items-center h-full">
                    <IconButton onClick={() => handleToggleStatus(params.row.id, params.row.status)} size="small">
                        {params.row.status ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={() => navigate(`/settings/threat/create/${params.row.id}`)} size="small">
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteConfirm(params.row.id)} size="small">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </div>
            ),
        },
    ];

    return (
        <ContentLayout fullWidth={true} position="relative" overflow="hidden" showMask={loading ? { msg: t('fetching_threat_alert') } : null}>
            <div className="flex justify-between gap-4">
                <div className="text-2xl font-medium">{t('manage_threat')}</div>
                <div className="flex-1 max-w-[600px]">
                    <TextField fullWidth label={t('search')} placeholder={t('threat_search_placeholder')} size="small" />
                </div>
                <Button variant="contained" onClick={() => navigate('/settings/threat/create')}>
                    {t('create_threat')}
                </Button>
            </div>
            <div className="mt-20">
                <DataTable height={600} columns={columns} rows={threatNotificationList} pageSize={10} />
            </div>
        </ContentLayout>
    );
};

export default ManageThreat;
